/**
 * Inline partner (contractor / installer) capture, for use inside another
 * workflow (WPS-163).
 *
 * Not every partner is in the tenant's partner network, so an agent who
 * cannot find one in search must be able to create it without abandoning the
 * registration or claim they are filling in. On save the partner becomes a
 * normal partnership - searchable from then on - and is handed straight back
 * to the caller to assign.
 *
 * Duplicate detection runs as the agent types and again server-side on
 * submit: a company name similar to an existing partner in the same city and
 * state raises a warning offering the existing partner, and only an explicit
 * "create anyway" proceeds. The backend answers 409 with the candidates if the
 * UI is bypassed, so the warning is enforced rather than advisory.
 *
 * Fields are generated from `FormFieldMeta` and rendered by the shared form
 * system: the address is an `ADDRESS` field (so it gets the platform address
 * widget with lookup and manual entry, never a bespoke address form) and the
 * extra-contacts editor is the one `custom` field, being a genuinely
 * composite widget rather than a standard input.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HardHat, Plus, Trash2, TriangleAlert } from 'lucide-react'

import {
  useCreateManualPartner,
  useUpdateManualPartner,
  type DuplicatePartnerCandidate,
  type ManualPartnerContactInput,
} from '@/admin/hooks/use-manual-partner'
import type { Partnership } from '@/admin/types/organisations'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { toastApiError } from '@/shared/lib/api-error'
import {
  buildFormFieldFromMeta,
  defineField,
  useForm,
  type FormFieldMeta,
} from '@/shared/lib/forms'

// The address widget hands back this shape; it is decomposed into the
// partner profile's individual attributes on submit.
interface AddressFieldValue {
  addressLine1?: string
  addressLine2?: string
  locality?: string
  region?: string
  postalCode?: string
  countryCode?: string
}

const LABEL_CLASS =
  'text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)]'

/**
 * Editor for contacts beyond the primary one. A repeatable group is not
 * expressible as a standard generated field, which is exactly what `custom`
 * is for.
 */
function AdditionalContactsEditor({
  contacts,
  onChange,
}: {
  contacts: ManualPartnerContactInput[]
  onChange: (next: ManualPartnerContactInput[]) => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      {contacts.map((contact, index) => (
        <div
          key={index}
          className="flex flex-col gap-2 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-2.5"
        >
          <div className="flex items-center justify-between">
            <span className={LABEL_CLASS}>
              {t('partnerCapture.contactN', 'Contact {{n}}', { n: index + 2 })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove contact ${index + 2}`}
              onClick={() => onChange(contacts.filter((_, i) => i !== index))}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            {(
              [
                ['name', t('partnerCapture.contactName', 'Contact Name')],
                ['email', t('partnerCapture.contactEmail', 'Contact Email')],
                ['phone', t('partnerCapture.contactPhone', 'Contact Phone')],
              ] as const
            ).map(([key, label]) => (
              <div key={key} className="flex flex-col gap-1">
                <Label className={LABEL_CLASS}>{label}</Label>
                <Input
                  value={contact[key] ?? ''}
                  type={key === 'email' ? 'email' : 'text'}
                  onChange={(event) =>
                    onChange(
                      contacts.map((c, i) =>
                        i === index ? { ...c, [key]: event.target.value } : c,
                      ),
                    )
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start px-0 text-[11px] text-[var(--green)]"
        onClick={() => onChange([...contacts, { name: '', email: '', phone: '' }])}
      >
        <Plus className="mr-1 h-3 w-3" />
        {t('partnerCapture.addAdditionalContact', 'Add Additional Contact')}
      </Button>
    </div>
  )
}


/** Read one attribute value off a partnership detail payload. */
function attrValue(partnership: Partnership | undefined, code: string): string {
  const raw = partnership?.attribute_values?.find((a) => a.code_name === code)?.value
  return raw === undefined || raw === null ? '' : String(raw)
}

/**
 * Turn an existing partner into the form's initial values, so "Edit
 * Contractor" opens on what is already recorded rather than a blank form.
 */
function prefillFrom(partnership: Partnership | undefined) {
  if (!partnership) return null
  const contacts = partnership.contacts ?? []
  return {
    company_name: partnership.downstream_organisation.name,
    address: {
      addressLine1: attrValue(partnership, 'address_line_1'),
      addressLine2: attrValue(partnership, 'address_line_2'),
      locality: attrValue(partnership, 'city'),
      region: attrValue(partnership, 'state'),
      postalCode: attrValue(partnership, 'postcode'),
      countryCode: '',
    },
    business_phone: attrValue(partnership, 'company_phone'),
    company_email: attrValue(partnership, 'company_email'),
    contact_name: contacts[0]?.name ?? '',
    contact_email: contacts[0]?.email ?? '',
    contact_phone: contacts[0]?.phone ?? '',
    extraContacts: contacts.slice(1).map((c) => ({
      name: c.name,
      email: c.email,
      phone: c.phone,
    })),
  }
}

function DuplicateWarning({
  candidates,
  onSelectExisting,
  onContinue,
  continuing,
}: {
  candidates: DuplicatePartnerCandidate[]
  onSelectExisting: (candidate: DuplicatePartnerCandidate) => void
  onContinue: () => void
  continuing: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2 rounded-[4px] border-[0.5px] border-[var(--status-warning)] bg-[var(--bg-warning)] p-3">
      <div className="flex items-center gap-1.5">
        <TriangleAlert className="h-3.5 w-3.5 text-[var(--status-warning)]" />
        <span className={LABEL_CLASS}>
          {t('partnerCapture.possibleDuplicate', 'Possible duplicate')}
        </span>
      </div>
      <p className="text-[12px] text-[var(--ink)]">
        {candidates.length === 1
          ? t(
              'partnerCapture.duplicateWarningSingle',
              'A partner with a similar name already exists in this city and state.',
            )
          : t(
              'partnerCapture.duplicateWarningMultiple',
              '{{count}} partners with similar names already exist in this city and state.',
              { count: candidates.length },
            )}
      </p>
      <div className="flex flex-col gap-1">
        {candidates.map((candidate) => (
          <div
            key={candidate.partnership_uuid}
            className="flex items-center justify-between gap-3 rounded-[4px] bg-[var(--bg)] px-2.5 py-2"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[12px] text-[var(--ink)]">
                {candidate.name}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">
                {[candidate.city, candidate.state].filter(Boolean).join(', ')}
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => onSelectExisting(candidate)}
            >
              {t('partnerCapture.selectExisting', 'Select existing')}
            </Button>
          </div>
        ))}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start px-0 text-[11px]"
        disabled={continuing}
        onClick={onContinue}
      >
        {continuing
          ? t('partnerCapture.creating', 'Creating...')
          : t('partnerCapture.continueCreating', 'Continue creating new partner')}
      </Button>
    </div>
  )
}

export function PartnerCreateInlineForm({
  roleUuid,
  roleName = 'Contractor',
  initialCompanyName = '',
  editing,
  variant = 'inline',
  onCreated,
  onSelectExisting,
  onCancel,
  onSearchExisting,
}: {
  roleUuid?: string | null
  /**
   * The partner being corrected. Present = edit mode: the form opens on the
   * existing values, saves with PATCH, and runs no duplicate check (a record
   * would only ever match itself).
   */
  editing?: Partnership | null
  /**
   * ``inline`` renders the form in its own dashed panel with a heading, for
   * embedding directly in a step. ``modal`` drops that chrome because the
   * surrounding ``PartnerCreateDialog`` supplies the frame and title.
   */
  variant?: 'inline' | 'modal'
  /** The tenant's own word for this partner type, used in the copy. */
  roleName?: string
  /** Prefills the company name with whatever the agent already typed. */
  initialCompanyName?: string
  onCreated: (partnership: Partnership) => void
  /** Picking an existing partner out of the duplicate warning. */
  onSelectExisting: (candidate: DuplicatePartnerCandidate) => void
  onCancel: () => void
  /** When supplied, the form offers a route back to the caller's existing-partner
   *  picker. Only surfaces where the caller HAS one (the claim contractor card
   *  does; the registration wizard does not), so no caller gains a dead link. */
  onSearchExisting?: () => void
}) {
  const { t } = useTranslation()
  const createPartner = useCreateManualPartner()
  const updatePartner = useUpdateManualPartner()
  const isEditing = !!editing
  const prefill = useMemo(() => prefillFrom(editing ?? undefined), [editing])
  const [extraContacts, setExtraContacts] = useState<ManualPartnerContactInput[]>(
    prefill?.extraContacts ?? [],
  )
  // Read through a ref inside the submit handler so `extraContacts` need not
  // be a `useForm` dependency. As a dependency it re-initialised the form on
  // every keystroke in an extra contact, which intermittently dropped the
  // rows the agent had just added.
  const extraContactsRef = useRef(extraContacts)
  useEffect(() => {
    extraContactsRef.current = extraContacts
  }, [extraContacts])
  // Set once the agent has seen the warning and chosen to proceed anyway.
  const [acknowledged, setAcknowledged] = useState(false)
  const [addressError, setAddressError] = useState<string | null>(null)
  // Duplicates the server rejected the save with, when the live check had not
  // surfaced them yet.
  const [serverDuplicates, setServerDuplicates] = useState<
    DuplicatePartnerCandidate[]
  >([])

  const fields = useMemo<FormFieldMeta[]>(
    () => [
      defineField({
        code_name: 'company_name',
        display_name: 'Company Name',
        field_type: 'TEXT',
        required: true,
      }),
      // ADDRESS gets the shared address widget (search + manual entry), which
      // is the only sanctioned way to capture an address.
      // Not flagged `required` to the generated validator: an ADDRESS value is
      // an object, which the generic required check cannot judge, and a failed
      // check aborts submitForm() with nothing shown to the user. City and
      // state are enforced explicitly on submit instead (and again by the
      // API), which is also where the duplicate key comes from.
      defineField({
        code_name: 'address',
        display_name: 'Company Address',
        field_type: 'ADDRESS',
      }),
      defineField({
        code_name: 'business_phone',
        display_name: 'Business Phone',
        field_type: 'PHONE',
      }),
      defineField({
        code_name: 'company_email',
        display_name: 'Company Email',
        field_type: 'EMAIL',
      }),
      defineField({
        code_name: 'contact_name',
        display_name: 'Contact Name',
        field_type: 'TEXT',
      }),
      defineField({
        code_name: 'contact_email',
        display_name: 'Contact Email',
        field_type: 'EMAIL',
      }),
      defineField({
        code_name: 'contact_phone',
        display_name: 'Contact Phone',
        field_type: 'PHONE',
      }),
    ],
    [],
  )

  // Carry over whatever the agent already typed into the search box, so they
  // do not type the company name twice.
  const generatedFields = useMemo(
    () =>
      fields.map((field) => {
        const initial = prefill
          ? (prefill as Record<string, unknown>)[field.code_name]
          : field.code_name === 'company_name'
            ? initialCompanyName
            : undefined
        return buildFormFieldFromMeta(field, initial)
      }),
    [fields, initialCompanyName, prefill],
  )

  const formConfig = useMemo(
    () => ({
      name: 'partner-create-inline',
      // Footer, then our own buttons drive submitForm() - matching how the
      // shared edit dialog does it. The generated form has no onChange hook,
      // so the live duplicate check reads values off the form context via the
      // render prop below instead.
      submit: 'footer' as const,
      fields: generatedFields,
    }),
    [generatedFields],
  )

  const { Form, ref } = useForm(
    formConfig,
    async (values: Record<string, unknown>) => {
      const address = (values.address ?? {}) as AddressFieldValue
      // City and state are the duplicate-detection key, so a partner cannot be
      // created without them. Reported here because the generated validator
      // cannot see inside the composite address value.
      if (!String(address.locality ?? '').trim() || !String(address.region ?? '').trim()) {
        setAddressError(
          t(
            'partnerCapture.addressRequired',
            'Enter the company address, including town / city and county / state.',
          ),
        )
        return
      }
      setAddressError(null)
      const contacts: ManualPartnerContactInput[] = [
        {
          name: String(values.contact_name ?? ''),
          email: String(values.contact_email ?? ''),
          phone: String(values.contact_phone ?? ''),
        },
        ...extraContactsRef.current,
      ].filter((contact) =>
        [contact.name, contact.email, contact.phone].some((v) => (v ?? '').trim()),
      )

      try {
        if (isEditing && editing) {
          const updated = await updatePartner.mutateAsync({
            partnershipUuid: editing.uuid,
            company_name: String(values.company_name ?? '').trim(),
            city: String(address.locality ?? '').trim(),
            state: String(address.region ?? '').trim(),
            address_line_1: String(address.addressLine1 ?? '').trim(),
            address_line_2: String(address.addressLine2 ?? '').trim(),
            postcode: String(address.postalCode ?? '').trim(),
            business_phone: String(values.business_phone ?? '').trim(),
            company_email: String(values.company_email ?? '').trim(),
            contacts,
          })
          onCreated(updated)
          return
        }
        const partnership = await createPartner.mutateAsync({
          company_name: String(values.company_name ?? '').trim(),
          city: String(address.locality ?? '').trim(),
          state: String(address.region ?? '').trim(),
          address_line_1: String(address.addressLine1 ?? '').trim(),
          address_line_2: String(address.addressLine2 ?? '').trim(),
          postcode: String(address.postalCode ?? '').trim(),
          business_phone: String(values.business_phone ?? '').trim(),
          company_email: String(values.company_email ?? '').trim(),
          contacts,
          role_uuid: roleUuid ?? undefined,
          acknowledge_duplicates: acknowledged,
        })
        onCreated(partnership)
      } catch (error) {
        // 409 means the server rejected the save as a duplicate. Render the
        // candidates so the agent can pick the existing partner or create
        // anyway, otherwise the click looks like it did nothing. The error
        // path is NOT run through the response interceptor's envelope unwrap,
        // so the body is the raw ``{ status, message, result }`` shape - read
        // the candidates off ``result``, not ``data``.
        const response = (
          error as {
            response?: {
              status?: number
              data?: { result?: { duplicates?: DuplicatePartnerCandidate[] } }
            }
          }
        )?.response
        if (response?.status === 409) {
          setServerDuplicates(response.data?.result?.duplicates ?? [])
          return
        }
        toastApiError(
          error,
          t('partnerCapture.errorSave', 'Could not save the {{role}}.', {
            role: roleName.toLowerCase(),
          }),
        )
      }
    },
    [acknowledged, roleUuid, isEditing, editing],
  )

  const heading = isEditing
    ? t('partnerCapture.editHeading', 'Edit {{role}}', {
        role: roleName.toLowerCase(),
      })
    : t('partnerCapture.createHeading', 'Create new {{role}}', {
        role: roleName.toLowerCase(),
      })

  const body = (
    <>
      <Form className="flex flex-col gap-3">
        {() => (
          <>
            <div className="flex flex-col gap-1.5">
              <span className={LABEL_CLASS}>
          {t('partnerCapture.additionalContacts', 'Additional contacts')}
        </span>
              <AdditionalContactsEditor
                contacts={extraContacts}
                onChange={setExtraContacts}
              />
            </div>
            {/* Duplicate detection is submit-time only: the search the agent
                came through already surfaces existing partners, so we do not
                re-check live as they type. If the server finds a match on save
                it answers 409 and we show the alert here, offering the existing
                partner or an explicit "create anyway" (WPS-163). */}
            {!isEditing && !acknowledged && serverDuplicates.length > 0 && (
              <DuplicateWarning
                candidates={serverDuplicates}
                onSelectExisting={onSelectExisting}
                continuing={createPartner.isPending}
                onContinue={() => {
                  setAcknowledged(true)
                  ref.current?.submitForm()
                }}
              />
            )}
          </>
        )}
      </Form>

      {addressError && (
        <p className="text-[11px] text-[var(--status-error)]">{addressError}</p>
      )}

      <div className="flex items-center justify-between gap-2 border-t-[0.5px] border-t-[var(--rule)] pt-2.5">
        {/* An agent who opened this from a search may realise the contractor
            already exists - most often a branch of a firm already on file.
            Without a way back they would have to cancel, losing what they had
            typed, so offer the picker explicitly. */}
        {onSearchExisting && !isEditing ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0"
            onClick={onSearchExisting}
          >
            {t('partnerCapture.searchExisting', 'Search existing instead')}
          </Button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={createPartner.isPending || updatePartner.isPending}
          onClick={() => ref.current?.submitForm()}
        >
          {createPartner.isPending || updatePartner.isPending
            ? t('partnerCapture.saving', 'Saving...')
            : isEditing
              ? t('partnerCapture.saveChanges', 'Save changes')
              : t('partnerCapture.saveAndAssign', 'Save {{role}} & assign', {
                  role: roleName.toLowerCase(),
                })}
        </Button>
        </div>
      </div>
    </>
  )

  if (variant === 'modal') {
    return <div className="flex flex-col gap-3">{body}</div>
  }

  return (
    <div className="mt-2 flex flex-col gap-3 rounded-[4px] border-[0.5px] border-dashed border-[var(--green)] bg-[var(--bg-subtle)] p-3">
      <div className="flex items-center gap-1.5">
        <HardHat className="h-3.5 w-3.5 text-[var(--ink-3)]" />
        <span className={LABEL_CLASS}>{heading}</span>
      </div>
      {body}
    </div>
  )
}

/**
 * The capture form as a modal (WPS-163). This is the one entry point for
 * creating or editing a partner mid-workflow: the search field's
 * "Add '<name>'" row and the assigned card's Edit button both open it, so the
 * flow never leaves the registration or claim it started from.
 */
export function PartnerCreateDialog({
  open,
  roleUuid,
  roleName = 'Contractor',
  initialCompanyName = '',
  editing,
  onCreated,
  onSelectExisting,
  onCancel,
  onSearchExisting,
}: {
  open: boolean
  roleUuid?: string | null
  roleName?: string
  initialCompanyName?: string
  editing?: Partnership | null
  onCreated: (partnership: Partnership) => void
  onSelectExisting: (candidate: DuplicatePartnerCandidate) => void
  onCancel: () => void
  /** Forwarded to the form; see its own prop docs. */
  onSearchExisting?: () => void
}) {
  const { t } = useTranslation()
  const isEditing = !!editing
  const heading = isEditing
    ? t('partnerCapture.editHeading', 'Edit {{role}}', {
        role: roleName.toLowerCase(),
      })
    : t('partnerCapture.createHeading', 'Create new {{role}}', {
        role: roleName.toLowerCase(),
      })
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5 text-[15px]">
            <HardHat className="h-4 w-4 text-[var(--ink-3)]" />
            {heading}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t(
              'partnerCapture.dialogDescription',
              'Capture the {{role}} details, including at least a company name, town / city and county / state.',
              { role: roleName.toLowerCase() },
            )}
          </DialogDescription>
        </DialogHeader>
        <PartnerCreateInlineForm
          variant="modal"
          roleUuid={roleUuid}
          roleName={roleName}
          initialCompanyName={initialCompanyName}
          editing={editing}
          onCreated={onCreated}
          onSelectExisting={onSelectExisting}
          onCancel={onCancel}
          onSearchExisting={onSearchExisting}
        />
      </DialogContent>
    </Dialog>
  )
}
